import { useState, useRef, useEffect, useCallback } from 'react';
import type { MatchFormat, GameMode, SetupConfig, CourseData, TeeBox } from '@/lib/golfEngine';
import { AVAILABLE_COURSE_DATA, buildCourseSetup, VERIFIED_COURSE_REGISTRY, checkApiResultAgainstRegistry } from '@/lib/golfEngine';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Users, User, DollarSign, MapPin, Flag, Info, Clock, CheckCircle2, Loader2, RotateCcw, ChevronRight, Shuffle, X, Search, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import logoImage from '@assets/2_down_auto_pic_1769442674853.png';
import { AuthButton } from '@/components/AuthButton';
import { useDashboardPending } from '@/hooks/use-round-lifecycle';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { CourseSearchModal } from '@/components/CourseSearchModal';

import type { GameState } from '@/lib/golfEngine';

interface DBCourse {
  id: string;
  canonicalName: string;
  displayName: string;
  city?: string;
  state?: string;
  country?: string;
  isVerified?: boolean;
  tees: DBCourseTee[];
}

interface DBCourseTee {
  id: string;
  courseId: string;
  teeName: string;
  teeColor: string;
  gender: string;
  courseRating: number;
  slopeRating: number;
  yardageTotal: number;
  parTotal: number;
  isVerified?: boolean;
  holes?: DBCourseHole[];
}

interface DBCourseHole {
  id: string;
  courseTeeId: string;
  holeNumber: number;
  par: number;
  yardage: number;
  handicapIndex: number;
}

function normalizeDBCourse(course: DBCourse): CourseData {
  const teeBoxes: TeeBox[] = course.tees.map((t) => {
    const holes = t.holes ?? [];
    const sortedHoles = [...holes].sort((a, b) => a.holeNumber - b.holeNumber);
    return {
      id: t.id,
      name: t.teeName,
      color: t.teeColor || '#666',
      gender: (t.gender === 'F' ? 'F' : 'M') as 'M' | 'F',
      courseRating: t.courseRating,
      slopeRating: t.slopeRating,
      yardages: sortedHoles.map((h) => h.yardage),
      unofficial: !t.isVerified,
    };
  });

  const firstTee = teeBoxes[0];
  const firstTeeHoles = (course.tees[0]?.holes ?? []).sort((a, b) => a.holeNumber - b.holeNumber);

  return {
    id: course.id,
    name: course.displayName || course.canonicalName,
    location: [course.city, course.state].filter(Boolean).join(', ') || '',
    pars: firstTeeHoles.map((h) => h.par),
    strokeIndexes: firstTeeHoles.map((h) => h.handicapIndex),
    teeBoxes,
  };
}

function validateCourseData(course: CourseData): { valid: boolean; error: string } {
  if (!course.teeBoxes || course.teeBoxes.length === 0) {
    return { valid: false, error: `${course.name} has no tees defined.` };
  }
  const holes = course.pars?.length ?? 0;
  if (holes !== 18) {
    return { valid: false, error: `${course.name} has ${holes} holes (needs 18).` };
  }
  const indexes = course.strokeIndexes ?? [];
  const uniqueIndexes = new Set(indexes);
  if (uniqueIndexes.size !== 18 || indexes.some((n) => n < 1 || n > 18)) {
    return { valid: false, error: `${course.name} has invalid stroke indexes (must be 1-18 unique).` };
  }
  const firstTee = course.teeBoxes[0];
  if (!firstTee.courseRating || !firstTee.slopeRating) {
    return { valid: false, error: `${course.name} is missing rating or slope.` };
  }
  return { valid: true, error: '' };
}

function buildCourseSnapshot(course: CourseData, teeBox: TeeBox): Record<string, any> {
  return {
    displayName: course.name,
    teeName: teeBox.name,
    courseRating: teeBox.courseRating,
    slopeRating: teeBox.slopeRating,
    holes: course.pars.map((par, i) => ({
      number: i + 1,
      par,
      yardage: teeBox.yardages[i] ?? 0,
      handicapIndex: course.strokeIndexes[i] ?? 0,
    })),
  };
}

const GAME_MODES: { label: string; value: GameMode; description: string }[] = [
  {
    label: "DoublePress!",
    value: "OnDemandPress",
    description: "You choose when to press — tap Press to open a new side bet on any Front, Back, or Overall leg. Press-on-press allowed on Front & Back; one Overall press per side.",
  },
  {
    label: "Two Down Auto",
    value: "TwoDownAuto",
    description: "Presses fire automatically when either side falls 2 holes behind on the Front or Back bet. Overall runs all 18 holes with no presses — just play and score.",
  },
];

interface SetupScreenProps {
  onStartRound: (config: SetupConfig) => void;
  existingGameState?: GameState | null;
  onResumeRound?: (config: SetupConfig) => void;
  onCancelEdit?: () => void;
  onLoadRound?: (roundId: string) => Promise<void> | void;
}

interface PlayerInput {
  name: string;
  handicap: string;
  teeBoxId: string;
  stableId?: string;
  // Tags the slot seeded as "the organizer" at Setup. Travels with this
  // object through Throw Balls (which swaps whole PlayerInput objects), so
  // roundSync.createRound() can find the organizer regardless of final slot.
  isOrganizer?: boolean;
  // Stable DB persons.id, populated only when the player was added via a
  // recent-player chip tap. Forwarded into GameState and then into the
  // createRound payload so the server can bind directly without name matching.
  personId?: string;
}

const LAST_SETUP_KEY = 'doublePress_lastSetup';

interface SavedSetup {
  matchFormat: MatchFormat;
  gameMode: GameMode;
  startingHole: 1 | 10;
  courseId: string;
  stakes: { front: string; back: string; overall: string };
  team1Players: [PlayerInput, PlayerInput];
  team2Players: [PlayerInput, PlayerInput];
}

function saveLastSetup(setup: SavedSetup) {
  try {
    localStorage.setItem(LAST_SETUP_KEY, JSON.stringify(setup));
  } catch {}
}

function loadLastSetup(): SavedSetup | null {
  try {
    const saved = localStorage.getItem(LAST_SETUP_KEY);
    if (saved) return JSON.parse(saved) as SavedSetup;
  } catch {}
  return null;
}

function getInitialValues(existingGameState?: GameState | null) {
  if (existingGameState) {
    const team1 = existingGameState.teams[0].players;
    const team2 = existingGameState.teams[1].players;
    return {
      matchFormat: existingGameState.matchFormat,
      gameMode: existingGameState.gameMode,
      startingHole: existingGameState.startingHole,
      courseId: existingGameState.courseDataId || '',
      stakes: {
        front: String(existingGameState.stakes.front),
        back: String(existingGameState.stakes.back),
        overall: String(existingGameState.stakes.overall),
      },
      team1Players: [
        { name: team1[0]?.name || '', handicap: String(team1[0]?.handicap || 0), teeBoxId: team1[0]?.teeBoxId || '', stableId: team1[0]?.id, isOrganizer: team1[0]?.isOrganizer },
        { name: team1[1]?.name || '', handicap: String(team1[1]?.handicap || 0), teeBoxId: team1[1]?.teeBoxId || '', stableId: team1[1]?.id, isOrganizer: team1[1]?.isOrganizer },
      ] as [PlayerInput, PlayerInput],
      team2Players: [
        { name: team2[0]?.name || '', handicap: String(team2[0]?.handicap || 0), teeBoxId: team2[0]?.teeBoxId || '', stableId: team2[0]?.id, isOrganizer: team2[0]?.isOrganizer },
        { name: team2[1]?.name || '', handicap: String(team2[1]?.handicap || 0), teeBoxId: team2[1]?.teeBoxId || '', stableId: team2[1]?.id, isOrganizer: team2[1]?.isOrganizer },
      ] as [PlayerInput, PlayerInput],
    };
  }

  const lastSetup = loadLastSetup();
  if (lastSetup) return lastSetup;

  return null;
}

export function SetupScreen({ onStartRound, existingGameState, onResumeRound, onCancelEdit, onLoadRound }: SetupScreenProps) {
  const isEditMode = !!existingGameState;
  const initial = getInitialValues(existingGameState);
  const { dashboard, isLoading: dashboardLoading } = useDashboardPending();
  const [loadingRoundId, setLoadingRoundId] = useState<string | null>(null);
  const [activePage, setActivePage] = useState(isEditMode ? 1 : 0);

  const scrollToPage = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  useEffect(() => {
    if (isEditMode) {
      setActivePage(1);
    }
  }, [isEditMode]);

  const handleLoadRound = async (roundId: string) => {
    if (!onLoadRound) return;
    setLoadingRoundId(roundId);
    try {
      await onLoadRound(roundId);
    } catch {
      setLoadingRoundId(null);
    }
  };

  const [matchFormat, setMatchFormat] = useState<MatchFormat>(initial?.matchFormat || '2v2');
  const [gameMode, setGameMode] = useState<GameMode>(initial?.gameMode || 'OnDemandPress');
  const [showOnDemandInfo, setShowOnDemandInfo] = useState(false);
  const [showTwoDownInfo, setShowTwoDownInfo] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showCourseSearch, setShowCourseSearch] = useState(false);
  const [pendingImportCourseId, setPendingImportCourseId] = useState<string | null>(null);
  const [coursePickerOpen, setCoursePickerOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const [apiCourseResults, setApiCourseResults] = useState<Array<{id:string;club_name:string;course_name:string;location?:{city?:string;state?:string}}>>([]);
  const [apiSearching, setApiSearching] = useState(false);
  const [importingApiCourseId, setImportingApiCourseId] = useState<string | null>(null);
  const courseSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();

  const { data: rawCourses = [], isLoading: coursesLoading, isError: coursesError } = useQuery<DBCourse[]>({
    queryKey: ['/api/courses/local'],
    staleTime: 0, // course definitions can change between sessions; always re-fetch on mount
  });

  // DB/API course IDs temporarily hidden in favour of their bundled hardcoded equivalents.
  // Matching rule: exact UUID — never fuzzy name matching.
  // To restore a DB course, remove its entry from this set.
  // Belt-and-suspenders: server already filters these, but keep both UUIDs
  // here too in case an older cached bundle bypasses the server block.
  const DB_COURSE_BLOCK_LIST = new Set<string>([
    '52173902-8935-4126-8e2d-dbc2dd080498', // Kenwood G&CC — dev DB UUID
    '069f1562-8064-4139-948b-005a3b6be0c1', // Kenwood G&CC — prod DB UUID
  ]);

  const allCourses = rawCourses.map(normalizeDBCourse);
  const usableDbCourses = allCourses
    .filter((c) => {
      if (DB_COURSE_BLOCK_LIST.has(c.id)) return false;
      const v = validateCourseData(c);
      return v.valid;
    });

  // Merge DB courses + hardcoded courses, deduplicated by name
  const dbNames = new Set(usableDbCourses.map(c => c.name.toLowerCase()));
  const mergedCourses = [
    ...usableDbCourses,
    ...AVAILABLE_COURSE_DATA.filter(hc => !dbNames.has(hc.name.toLowerCase())),
  ].sort((a, b) => {
    // International Country Club always first
    if (a.name === 'International Country Club') return -1;
    if (b.name === 'International Country Club') return 1;
    return a.name.localeCompare(b.name);
  });
  const usableCourses = mergedCourses;

  const findInitialCourse = (): CourseData => {
    if (initial?.courseId) {
      const found = usableCourses.find((cd) => cd.id === initial.courseId);
      if (found) return found;
      const legacy = AVAILABLE_COURSE_DATA.find((cd) => cd.id === initial.courseId);
      if (legacy) return legacy;
    }
    if (existingGameState) {
      const byHoles = usableCourses.find((cd) =>
        existingGameState.holes.every((h, i) => h.par === cd.pars[i])
      );
      if (byHoles) return byHoles;
      const legacy = AVAILABLE_COURSE_DATA.find((cd) =>
        existingGameState.holes.every((h, i) => h.par === cd.pars[i])
      );
      if (legacy) return legacy;
    }
    if (usableCourses.length > 0) return usableCourses[0];
    return AVAILABLE_COURSE_DATA[0];
  };

  const [selectedCourse, setSelectedCourse] = useState<CourseData>(findInitialCourse());
  const [startingHole, setStartingHole] = useState<1 | 10>(initial?.startingHole || 1);

  const defaultTeeId = selectedCourse.teeBoxes.find((t) => t.id === 'black')?.id || selectedCourse.teeBoxes[0]?.id || '';

  const [team1Players, setTeam1Players] = useState<[PlayerInput, PlayerInput]>(
    initial?.team1Players || [
      // Default seed for a brand-new round: slot 0 is the organizer until
      // Throw Balls or manual edits move it. The flag travels with this
      // object, not the index, from here on.
      { name: '', handicap: '', teeBoxId: defaultTeeId, isOrganizer: true },
      { name: '', handicap: '', teeBoxId: defaultTeeId },
    ]
  );
  const [team2Players, setTeam2Players] = useState<[PlayerInput, PlayerInput]>(
    initial?.team2Players || [
      { name: '', handicap: '', teeBoxId: defaultTeeId },
      { name: '', handicap: '', teeBoxId: defaultTeeId },
    ]
  );
  const [stakes, setStakes] = useState(
    initial?.stakes || { front: '10', back: '10', overall: '10' }
  );

  const mensTees = selectedCourse.teeBoxes.filter(t => t.gender === 'M');
  const ladiesTees = selectedCourse.teeBoxes.filter(t => t.gender === 'F');

  const updatePlayer = (
    team: 'team1' | 'team2',
    index: number,
    field: 'name' | 'handicap' | 'teeBoxId',
    value: string
  ) => {
    if (team === 'team1') {
      const updated = [...team1Players] as [PlayerInput, PlayerInput];
      updated[index] = { ...updated[index], [field]: value };
      setTeam1Players(updated);
    } else {
      const updated = [...team2Players] as [PlayerInput, PlayerInput];
      updated[index] = { ...updated[index], [field]: value };
      setTeam2Players(updated);
    }
  };

  const buildConfig = (courseOverride?: CourseData): SetupConfig => {
    const course = courseOverride ?? selectedCourse;
    const players: SetupConfig['players'] = [];
    const firstTeeBox = course.teeBoxes.find(t => t.id === team1Players[0].teeBoxId) || course.teeBoxes[0];

    if (matchFormat === '2v2') {
      players.push(
        { name: team1Players[0].name || 'Player 1', handicap: parseFloat(team1Players[0].handicap) || 0, teamIndex: 0, teeBoxId: team1Players[0].teeBoxId || defaultTeeId, stableId: team1Players[0].stableId, isOrganizer: team1Players[0].isOrganizer, personId: team1Players[0].personId },
        { name: team1Players[1].name || 'Player 2', handicap: parseFloat(team1Players[1].handicap) || 0, teamIndex: 0, teeBoxId: team1Players[1].teeBoxId || defaultTeeId, stableId: team1Players[1].stableId, isOrganizer: team1Players[1].isOrganizer, personId: team1Players[1].personId },
        { name: team2Players[0].name || 'Player 3', handicap: parseFloat(team2Players[0].handicap) || 0, teamIndex: 1, teeBoxId: team2Players[0].teeBoxId || defaultTeeId, stableId: team2Players[0].stableId, isOrganizer: team2Players[0].isOrganizer, personId: team2Players[0].personId },
        { name: team2Players[1].name || 'Player 4', handicap: parseFloat(team2Players[1].handicap) || 0, teamIndex: 1, teeBoxId: team2Players[1].teeBoxId || defaultTeeId, stableId: team2Players[1].stableId, isOrganizer: team2Players[1].isOrganizer, personId: team2Players[1].personId }
      );
    } else {
      players.push(
        { name: team1Players[0].name || 'Player A', handicap: parseFloat(team1Players[0].handicap) || 0, teamIndex: 0, teeBoxId: team1Players[0].teeBoxId || defaultTeeId, stableId: team1Players[0].stableId, isOrganizer: team1Players[0].isOrganizer, personId: team1Players[0].personId },
        { name: team2Players[0].name || 'Player B', handicap: parseFloat(team2Players[0].handicap) || 0, teamIndex: 1, teeBoxId: team2Players[0].teeBoxId || defaultTeeId, stableId: team2Players[0].stableId, isOrganizer: team2Players[0].isOrganizer, personId: team2Players[0].personId }
      );
    }

    const courseSetup = buildCourseSetup(course, firstTeeBox);
    const courseSnapshot = buildCourseSnapshot(course, firstTeeBox);
    const courseId = usableCourses.find((c) => c.id === course.id)?.id;

    return {
      matchFormat,
      gameMode,
      course: courseSetup,
      courseData: course,
      players,
      stakes: {
        front: parseFloat(stakes.front) || 10,
        back: parseFloat(stakes.back) || 10,
        overall: parseFloat(stakes.overall) || 20,
      },
      startingHole,
      courseId,
      courseSnapshot,
    };
  };

  const [courseRefreshError, setCourseRefreshError] = useState<string | null>(null);
  const [isRefreshingCourse, setIsRefreshingCourse] = useState(false);
  const [showGuestWarning, setShowGuestWarning] = useState(false);
  const pendingStartConfig = useRef<ReturnType<typeof buildConfig> | null>(null);

  const handleStart = async () => {
    const validation = validateCourseData(selectedCourse);
    if (!validation.valid) {
      setValidationError(validation.error);
      return;
    }
    setValidationError(null);
    setCourseRefreshError(null);

    // DB/API-backed courses: foreground refetch required before baking holes into
    // GameState. If the fetch fails we block — never fall back to stale cache data.
    // Hardcoded bundled courses (AVAILABLE_COURSE_DATA) proceed offline as normal.
    const isDBCourse = !AVAILABLE_COURSE_DATA.some(c => c.id === selectedCourse.id);
    let confirmedCourse: CourseData = selectedCourse;

    if (isDBCourse) {
      setIsRefreshingCourse(true);
      try {
        const fresh = await queryClient.fetchQuery<DBCourse[]>({
          queryKey: ['/api/courses/local'],
          staleTime: 0,
        });
        const freshCourse = fresh.map(normalizeDBCourse).find(c => c.id === selectedCourse.id);
        if (!freshCourse) {
          setCourseRefreshError(`Could not confirm "${selectedCourse.name}" course data. Tap Start Round to try again.`);
          setIsRefreshingCourse(false);
          return;
        }
        confirmedCourse = freshCourse;
      } catch {
        setCourseRefreshError(`Could not load course data for "${selectedCourse.name}". Check your connection and tap Start Round to try again.`);
        setIsRefreshingCourse(false);
        return;
      }
      setIsRefreshingCourse(false);
    }

    const config = buildConfig(confirmedCourse);

    saveLastSetup({
      matchFormat,
      gameMode,
      startingHole,
      courseId: confirmedCourse.id,
      stakes,
      team1Players,
      team2Players,
    });

    if (isEditMode && onResumeRound) {
      onResumeRound(config);
      return;
    }

    // Gate: if auth hasn't resolved to signed-in, warn before creating a local round
    if (!isAuthenticated && !authLoading) {
      pendingStartConfig.current = config;
      setShowGuestWarning(true);
      return;
    }

    onStartRound(config);
  };

  const handlePlayAnyway = () => {
    setShowGuestWarning(false);
    if (pendingStartConfig.current) {
      onStartRound(pendingStartConfig.current);
      pendingStartConfig.current = null;
    }
  };

  const runApiCourseSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setApiCourseResults([]); return; }
    setApiSearching(true);
    try {
      const res = await fetch(`/api/courses/api-search?q=${encodeURIComponent(q.trim())}`, { credentials: 'include' });
      if (!res.ok) { setApiCourseResults([]); return; }
      const data = await res.json();
      setApiCourseResults(data.results ?? []);
    } catch { setApiCourseResults([]); }
    finally { setApiSearching(false); }
  }, []);

  const handleCourseSearchQueryChange = useCallback((v: string) => {
    setCourseSearchQuery(v);
    if (courseSearchDebounce.current) clearTimeout(courseSearchDebounce.current);
    if (v.trim().length >= 2) {
      courseSearchDebounce.current = setTimeout(() => runApiCourseSearch(v), 500);
    } else {
      setApiCourseResults([]);
      setApiSearching(false);
    }
  }, [runApiCourseSearch]);

  const handleImportApiCourse = useCallback(async (apiCourse: {id:string;club_name:string;course_name:string}) => {
    setImportingApiCourseId(apiCourse.id);
    try {
      const res = await fetch('/api/courses/api-import', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiCourseId: apiCourse.id }),
      });
      const data = await res.json();
      if (!data.success) return;
      await queryClient.invalidateQueries({ queryKey: ['/api/courses/local'] });
      setPendingImportCourseId(data.courseId);
      setCoursePickerOpen(false);
      setCourseSearchQuery('');
      setApiCourseResults([]);
    } catch { /* silent — course will still be selectable on next open */ }
    finally { setImportingApiCourseId(null); }
  }, [queryClient]);

  const handleCourseChange = (courseId: string) => {
    const course = usableCourses.find((c) => c.id === courseId) || AVAILABLE_COURSE_DATA.find((c) => c.id === courseId);
    if (course) {
      setCourseRefreshError(null);
      // DB courses: invalidate so the background refetch fires immediately and the
      // pre-submit gate has the freshest data when the user taps Start Round.
      if (!AVAILABLE_COURSE_DATA.some(c => c.id === courseId)) {
        queryClient.invalidateQueries({ queryKey: ['/api/courses/local'] });
      }
      setSelectedCourse(course);
      setValidationError(null);
      const newDefaultTee = course.teeBoxes.find((t) => t.id === 'black')?.id || course.teeBoxes[0]?.id || '';
      setTeam1Players([
        { ...team1Players[0], teeBoxId: newDefaultTee },
        { ...team1Players[1], teeBoxId: newDefaultTee },
      ]);
      setTeam2Players([
        { ...team2Players[0], teeBoxId: newDefaultTee },
        { ...team2Players[1], teeBoxId: newDefaultTee },
      ]);
    }
  };

  // Auto-select a newly imported course once the local courses list refreshes
  useEffect(() => {
    if (!pendingImportCourseId) return;
    const match = usableCourses.find((c) => c.id === pendingImportCourseId);
    if (match) {
      handleCourseChange(match.id);
      setPendingImportCourseId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usableCourses, pendingImportCourseId]);

  const handleStartFresh = () => {
    const freshTeeId = selectedCourse.teeBoxes.find(t => t.id === 'black')?.id || selectedCourse.teeBoxes[0]?.id || '';
    setTeam1Players([
      { name: '', handicap: '', teeBoxId: freshTeeId },
      { name: '', handicap: '', teeBoxId: freshTeeId },
    ]);
    setTeam2Players([
      { name: '', handicap: '', teeBoxId: freshTeeId },
      { name: '', handicap: '', teeBoxId: freshTeeId },
    ]);
    localStorage.removeItem(LAST_SETUP_KEY);
  };

  const totalPar = selectedCourse.pars.reduce((sum, p) => sum + p, 0);
  const displayTee = selectedCourse.teeBoxes.find(t => t.id === 'black') || selectedCourse.teeBoxes[0];

  // ── Recent players (signed-in users only) ─────────────────────────────────
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { data: recentPlayers = [] } = useQuery<{ personId: string; displayName: string; handicapIndex: number | null }[]>({
    queryKey: ['/api/contacts/recents'],
    enabled: isAuthenticated,
  });

  const allEnteredNames = [
    team1Players[0].name.trim(),
    team1Players[1].name.trim(),
    team2Players[0].name.trim(),
    team2Players[1].name.trim(),
  ].filter(Boolean).map(n => n.toLowerCase());

  // Diff B: ref mirror of player state — always reflects the most recently committed
  // slot values, even between React renders. addFromRecent reads this instead of the
  // render-time closure so rapid consecutive taps always target distinct slots.
  const playersRef = useRef({ team1: team1Players, team2: team2Players });
  // Keep ref in sync on every render (assignment, not a hook call — safe here).
  playersRef.current = { team1: team1Players, team2: team2Players };

  // Touch-tracking refs for scroll-vs-tap discrimination on chip row
  const chipTouchRef = useRef<{ x: number; y: number } | null>(null);
  const chipTouchFiredRef = useRef(false);
  const CHIP_SCROLL_THRESHOLD = 8; // px — horizontal move beyond this = scroll, not tap

  const removeFromSlot = (displayName: string) => {
    const normalized = displayName.trim().toLowerCase();
    setTeam1Players(prev => {
      const u = [...prev] as [PlayerInput, PlayerInput];
      for (let i = 0; i < 2; i++) {
        if (u[i].name.trim().toLowerCase() === normalized) u[i] = { ...u[i], name: '', handicap: '' };
      }
      return u;
    });
    setTeam2Players(prev => {
      const u = [...prev] as [PlayerInput, PlayerInput];
      for (let i = 0; i < 2; i++) {
        if (u[i].name.trim().toLowerCase() === normalized) u[i] = { ...u[i], name: '', handicap: '' };
      }
      return u;
    });
  };

  const handleChipTap = (displayName: string, handicapIndex: number | null, alreadyAdded: boolean, personId: string) => {
    if (alreadyAdded) {
      removeFromSlot(displayName);
    } else {
      addFromRecent(displayName, handicapIndex, personId);
    }
  };

  const addFromRecent = (displayName: string, handicapIndex: number | null, personId?: string) => {
    // Diff B: read from ref, not closure — always current even before React re-renders
    const { team1: t1, team2: t2 } = playersRef.current;
    const slots = [
      { team: 'team1' as const, index: 0, player: t1[0] },
      { team: 'team1' as const, index: 1, player: t1[1] },
      { team: 'team2' as const, index: 0, player: t2[0] },
      { team: 'team2' as const, index: 1, player: t2[1] },
    ];
    // Prefer a truly empty slot first. If all slots are pre-filled from a
    // previous session, fall back to the first slot whose current name is NOT
    // already a recent-player name (i.e. an old stale name that can be replaced).
    let target = slots.find(s => !s.player.name.trim());
    if (!target) {
      const recentNames = new Set(recentPlayers.map(p => p.displayName.trim().toLowerCase()));
      target = slots.find(s => !recentNames.has(s.player.name.trim().toLowerCase()));
    }
    if (!target) return;
    // Single atomic update — set name AND handicap (AND personId when known)
    // in one setState call so the second field doesn't overwrite the first
    // via a stale closure.
    const { team, index } = target;
    const patch: Partial<PlayerInput> = { name: displayName };
    if (handicapIndex != null) patch.handicap = String(handicapIndex);
    if (personId) patch.personId = personId;
    if (team === 'team1') {
      // Diff B: optimistic ref update so the next tap (before re-render) sees slot as filled
      const next1 = [...playersRef.current.team1] as [PlayerInput, PlayerInput];
      next1[index] = { ...next1[index], ...patch };
      playersRef.current = { ...playersRef.current, team1: next1 };
      setTeam1Players(prev => {
        const updated = [...prev] as [PlayerInput, PlayerInput];
        updated[index] = { ...updated[index], ...patch };
        return updated;
      });
    } else {
      // Diff B: optimistic ref update so the next tap (before re-render) sees slot as filled
      const next2 = [...playersRef.current.team2] as [PlayerInput, PlayerInput];
      next2[index] = { ...next2[index], ...patch };
      playersRef.current = { ...playersRef.current, team2: next2 };
      setTeam2Players(prev => {
        const updated = [...prev] as [PlayerInput, PlayerInput];
        updated[index] = { ...updated[index], ...patch };
        return updated;
      });
    }
  };

  const handleRandomTeams = () => {
    const originalTeam1 = new Set([team1Players[0].name, team1Players[1].name]);
    const all: PlayerInput[] = [team1Players[0], team1Players[1], team2Players[0], team2Players[1]];
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    // If the shuffle landed on the same team pairing (just reordered), force a cross-swap
    const newTeam1 = new Set([all[0].name, all[1].name]);
    const sameTeams = originalTeam1.size === newTeam1.size && [...originalTeam1].every(n => newTeam1.has(n));
    if (sameTeams) {
      [all[1], all[2]] = [all[2], all[1]];
    }
    setTeam1Players([all[0], all[1]]);
    setTeam2Players([all[2], all[3]]);
  };

  const allFourFilled = matchFormat === '2v2'
    && !!team1Players[0].name.trim() && !!team1Players[1].name.trim()
    && !!team2Players[0].name.trim() && !!team2Players[1].name.trim();

  const [throwBallsInfoOpen, setThrowBallsInfoOpen] = useState(false);

  const getTeeLabel = (tee: TeeBox) => {
    const base = `${tee.name} (${tee.courseRating}/${tee.slopeRating})`;
    return tee.unofficial ? `${base} *` : base;
  };

  const renderPlayerInput = (
    team: 'team1' | 'team2',
    index: number,
    player: PlayerInput,
    playerLabel: string
  ) => (
    <div className="flex gap-2 items-center">
      <span className="text-sm text-gray-600 w-7 flex-shrink-0">{playerLabel}</span>
      <Input
        id={`${team}-${index}-name`}
        placeholder="Name"
        value={player.name}
        onChange={(e) => updatePlayer(team, index, 'name', e.target.value)}
        className="h-9 text-sm flex-1 min-w-0"
        data-testid={`input-${team}-player${index + 1}-name`}
      />
      <Input
        id={`${team}-${index}-hcp`}
        type="number"
        inputMode="decimal"
        placeholder="Index"
        step="0.1"
        min="0"
        max="54"
        value={player.handicap}
        onChange={(e) => {
          const raw = e.target.value;
          const dotIdx = raw.indexOf('.');
          const cleaned = dotIdx !== -1
            ? raw.slice(0, dotIdx + 2)
            : raw;
          updatePlayer(team, index, 'handicap', cleaned);
        }}
        className="h-9 text-sm text-center w-20"
        data-testid={`input-${team}-player${index + 1}-handicap`}
      />
      <Select
        value={player.teeBoxId}
        onValueChange={(v) => updatePlayer(team, index, 'teeBoxId', v)}
      >
        <SelectTrigger className="h-9 text-sm px-2 w-24" data-testid={`select-${team}-player${index + 1}-tee`}>
          <SelectValue placeholder="Tees" />
        </SelectTrigger>
          <SelectContent>
            {mensTees.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs font-semibold text-gray-600">Men's</div>
                {mensTees.map((tee) => (
                  <SelectItem key={tee.id} value={tee.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full border border-gray-300" 
                        style={{ backgroundColor: tee.color }}
                      />
                      {getTeeLabel(tee)}
                    </div>
                  </SelectItem>
                ))}
              </>
            )}
            {ladiesTees.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs font-semibold text-gray-600 mt-1">Ladies'</div>
                {ladiesTees.map((tee) => (
                  <SelectItem key={tee.id} value={tee.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full border border-gray-300" 
                        style={{ backgroundColor: tee.color }}
                      />
                      {getTeeLabel(tee)}
                    </div>
                  </SelectItem>
                ))}
              </>
            )}
            {selectedCourse.teeBoxes.some(t => t.unofficial) && (
              <div className="px-2 py-1 text-xs text-gray-500 border-t mt-1">* Unofficial — unrated</div>
            )}
          </SelectContent>
        </Select>
    </div>
  );

  return (
    <div className="bg-gray-50 overflow-hidden" style={{ minHeight: '100dvh', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* Page-slide container — CSS transform based; navigation via explicit buttons only.
          Outer swipe handlers removed: the leading hypothesis for the intermittent iOS PWA
          chip-tap failure is that onTouchStart on a parent element triggers the native
          gesture recognizer's disambiguation path, which — combined with touchAction:pan-x
          on child chips — can delay or suppress tap delivery on real iOS hardware. This
          cannot be confirmed in Chromium or headless testing; physical-device verification
          is the acceptance gate. */}
      <div
        className="relative overflow-hidden"
        style={{ height: 'calc(100dvh - env(safe-area-inset-top, 0px))' }}
      >
        {/* PAGE 1: Landing / Branding */}
        <div
          className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(${activePage === 0 ? '0%' : '-100%'})` }}
        >
          <div className="min-h-screen flex flex-col items-center justify-center px-6 py-8">
            <div className="flex justify-end w-full max-w-sm mb-4">
              <AuthButton />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-3 italic">Don't Get Pencil Whipped!</p>
            <img src={logoImage} alt="DoublePress" className="w-56 h-56 rounded-xl shadow-lg" />
            <h1 className="text-3xl font-extrabold mt-4 text-center tracking-tight">
              <span className="text-[#064E3B]">Double</span><span className="bg-[#064E3B] text-[#FACC15] px-1.5 rounded">Press</span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">Golf Betting Scorecard</p>
            <p className="text-base font-semibold text-gray-800 mt-5 text-center">Every bet tracked. Every hole settled.</p>
            <p className="text-xs text-gray-500 mt-1 text-center font-bold italic">Built to look and feel like the scorecard you already use.</p>
            <div className="mt-5 p-4 border border-gray-300 rounded-lg bg-white text-left w-full max-w-sm">
              <ul className="text-sm text-gray-700 space-y-1.5">
                <li>• Year-to-date and monthly win/loss totals</li>
                <li>• Exact records vs your regular playing partners
                  <p className="ml-4 text-gray-500 italic text-xs">"You're 7–3 and +$240 vs Mike"</p>
                </li>
                <li>• Team vs team performance</li>
                <li>• A balance-sheet view of your golf betting life</li>
              </ul>
            </div>
            <Button
              onClick={() => scrollToPage(1)}
              className="mt-6 h-12 px-8 text-base font-semibold bg-[#064E3B] hover:bg-[#053f30]"
              data-testid="button-new-round-landing"
            >
              Start New Round
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>

            {/* Page dots */}
            <div className="flex gap-2 mt-6">
              <div className={cn('w-2 h-2 rounded-full transition-colors', activePage === 0 ? 'bg-[#064E3B]' : 'bg-gray-300')} />
              <div className={cn('w-2 h-2 rounded-full transition-colors', activePage === 1 ? 'bg-[#064E3B]' : 'bg-gray-300')} />
            </div>
          </div>
        </div>

        {/* PAGE 2: Setup Form */}
        <div
          className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(${activePage === 0 ? '100%' : '0%'})` }}
        >
          <div className="px-4 py-4 pb-28 max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => scrollToPage(0)}
                className="flex items-center gap-1 text-xs font-semibold text-[#064E3B]"
                data-testid="button-back-to-landing"
              >
                <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                DoublePress
              </button>
              <div className="flex gap-2">
                <div className={cn('w-2 h-2 rounded-full transition-colors', activePage === 0 ? 'bg-[#064E3B]' : 'bg-gray-300')} />
                <div className={cn('w-2 h-2 rounded-full transition-colors', activePage === 1 ? 'bg-[#064E3B]' : 'bg-gray-300')} />
              </div>
              <AuthButton />
            </div>
          <div className="space-y-2">
            {isEditMode && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                <p className="text-sm font-semibold text-amber-800" data-testid="text-edit-mode-banner">Editing Match Settings</p>
                <p className="text-xs text-amber-700">Update any settings and tap "Apply Changes & Resume" to continue your round with the new values.</p>
              </div>
            )}
            {!isEditMode && !dashboardLoading && dashboard?.hasItems && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 space-y-2" data-testid="dashboard-pending-rounds">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-amber-600" />
                  Rounds Needing Attention
                </p>
                {dashboard.pendingFinalization.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Awaiting Finalization</p>
                    {dashboard.pendingFinalization.map((r) => (
                      <button
                        key={r.id}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors text-left disabled:opacity-60"
                        onClick={() => handleLoadRound(r.id)}
                        disabled={loadingRoundId === r.id}
                        data-testid={`pending-round-${r.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Flag className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{r.courseName}</p>
                            {r.createdAt && (
                              <p className="text-xs text-gray-500">{new Date(r.createdAt).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                        {loadingRoundId === r.id ? (
                          <Loader2 className="w-3.5 h-3.5 text-amber-600 animate-spin flex-shrink-0" />
                        ) : (
                          <span className="text-xs text-amber-700 font-medium flex-shrink-0">Finalize →</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Row 1: Match Format and Game Mode side by side */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
                <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Match Format</h2>
                <RadioGroup
                  value={matchFormat}
                  onValueChange={(v) => setMatchFormat(v as MatchFormat)}
                  className="flex gap-2"
                >
                  <Label
                    htmlFor="format-2v2"
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1 p-2 rounded-lg border-2 cursor-pointer transition-all',
                      matchFormat === '2v2' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <RadioGroupItem value="2v2" id="format-2v2" className="sr-only" />
                    <Users className={cn('w-4 h-4', matchFormat === '2v2' ? 'text-blue-600' : 'text-gray-400')} />
                    <span className="font-semibold text-gray-900 text-sm">2v2</span>
                  </Label>
                  <Label
                    htmlFor="format-1v1"
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1 p-2 rounded-lg border-2 cursor-pointer transition-all',
                      matchFormat === '1v1' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <RadioGroupItem value="1v1" id="format-1v1" className="sr-only" />
                    <User className={cn('w-4 h-4', matchFormat === '1v1' ? 'text-blue-600' : 'text-gray-400')} />
                    <span className="font-semibold text-gray-900 text-sm">1v1</span>
                  </Label>
                </RadioGroup>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
                <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Game Mode</h2>
                <div className="flex items-center gap-2">
                  <Select value={gameMode} onValueChange={(v) => setGameMode(v as GameMode)}>
                    <SelectTrigger className="h-9 text-sm font-medium flex-1" data-testid="select-game-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GAME_MODES.map((mode) => (
                        <SelectItem key={mode.value} value={mode.value} data-testid={`gamemode-option-${mode.value}`}>
                          {mode.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      gameMode === 'OnDemandPress' ? setShowOnDemandInfo(true) : setShowTwoDownInfo(true);
                    }}
                    className="flex-shrink-0 p-1"
                    data-testid="button-gamemode-info"
                  >
                    <Info className="w-4 h-4 text-blue-400 hover:text-blue-600 transition-colors" />
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: Course Selection */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-3 h-3 text-gray-500 flex-shrink-0" />
                <Popover open={coursePickerOpen} onOpenChange={setCoursePickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="h-7 text-xs flex items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 w-[200px] hover:bg-accent hover:text-accent-foreground transition-colors"
                      data-testid="select-course"
                    >
                      <span className="truncate">{selectedCourse.name || 'Select a course'}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3 ml-2 opacity-50"><path d="m6 9 6 6 6-6"/></svg>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[260px] p-0" align="start">
                    <Command
                      filter={(value, search) => {
                        if (search.length === 0) return 1;
                        const term = search.toLowerCase().trim();
                        const candidate = value.toLowerCase();
                        return candidate.includes(term) ? 1 : 0;
                      }}
                    >
                      <CommandInput
                        placeholder="Type any course name…"
                        className="h-8 text-xs"
                        data-testid="input-course-search"
                        value={courseSearchQuery}
                        onValueChange={handleCourseSearchQueryChange}
                      />
                      <CommandList className="max-h-[320px] overflow-y-auto">
                        {coursesLoading && (
                          <CommandEmpty className="text-xs text-gray-500 py-3">Loading courses...</CommandEmpty>
                        )}
                        {!coursesLoading && coursesError && (
                          <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
                            Couldn't load saved courses — showing defaults only.
                          </div>
                        )}

                        {/* Local courses */}
                        {(coursesError ? AVAILABLE_COURSE_DATA : usableCourses).map((course) => (
                          <CommandItem
                            key={course.id}
                            value={course.name}
                            onSelect={() => {
                              handleCourseChange(course.id);
                              setCoursePickerOpen(false);
                              setCourseSearchQuery('');
                              setApiCourseResults([]);
                            }}
                            className="text-xs"
                            data-testid={`course-option-${course.id}`}
                          >
                            <span className="truncate">{course.name}</span>
                            {selectedCourse.id === course.id && (
                              <CheckCircle2 className="w-3 h-3 ml-auto text-[#064E3B]" />
                            )}
                          </CommandItem>
                        ))}

                        {/* Inline API results — auto-appear as user types */}
                        {courseSearchQuery.trim().length >= 2 && (
                          <>
                            {apiSearching && (
                              <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400 border-t border-gray-100">
                                <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                                Searching 30,000+ courses…
                              </div>
                            )}
                            {!apiSearching && apiCourseResults.length > 0 && (() => {
                              // Filter API results against the verified course registry.
                              // Only a definite MATCH (name alias + location confirms same physical course)
                              // is suppressed. AMBIGUOUS results (name matches but no location data to
                              // confirm or deny) remain visible so the user can still import if they want.
                              // Results at a different location (e.g. UK Keswick) are always kept.
                              const filteredResults = apiCourseResults.filter((r) => {
                                const matchResult = checkApiResultAgainstRegistry(
                                  r.club_name,
                                  r.course_name,
                                  r.location,
                                  r.id,
                                );
                                // Keep 'no_match' AND 'ambiguous'; only suppress definite 'match'.
                                return matchResult.status !== 'match';
                              });

                              if (filteredResults.length === 0) return null;

                              return (
                                <div className="border-t border-gray-100">
                                  <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Add course</p>
                                  {filteredResults.map((r) => {
                                    const isImporting = importingApiCourseId === r.id;
                                    const name = (r.club_name === r.course_name || !r.course_name) ? r.club_name : `${r.club_name} — ${r.course_name}`;
                                    const loc = [r.location?.city, r.location?.state].filter(Boolean).join(', ');
                                    return (
                                      <div
                                        key={r.id}
                                        className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                        data-testid={`api-course-result-${r.id}`}
                                        onClick={() => !isImporting && handleImportApiCourse(r)}
                                      >
                                        <div className="min-w-0">
                                          <p className="text-xs font-medium text-gray-900 truncate">{name}</p>
                                          {loc && <p className="text-[10px] text-gray-400">{loc}</p>}
                                        </div>
                                        <button
                                          type="button"
                                          disabled={isImporting}
                                          className="flex-shrink-0 flex items-center gap-1 text-[10px] font-semibold text-[#064E3B] bg-emerald-50 hover:bg-emerald-100 rounded px-2 py-0.5 disabled:opacity-50"
                                          data-testid={`button-add-api-course-${r.id}`}
                                          onClick={(e) => { e.stopPropagation(); if (!isImporting) handleImportApiCourse(r); }}
                                        >
                                          {isImporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><span>+</span> Add</>}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            {!apiSearching && apiCourseResults.length === 0 && courseSearchQuery.trim().length >= 3 && (
                              <CommandEmpty className="text-xs text-gray-400 py-3">
                                No courses found — try just the club name
                              </CommandEmpty>
                            )}
                          </>
                        )}

                        {!courseSearchQuery.trim() && !coursesLoading && usableCourses.length === 0 && AVAILABLE_COURSE_DATA.length === 0 && (
                          <CommandEmpty className="text-xs text-gray-500 py-3">No courses available</CommandEmpty>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <div className="flex gap-2 text-center flex-shrink-0">
                  <div className="bg-gray-50 rounded px-2 py-0.5">
                    <span className="text-xs text-gray-500">R:</span>
                    <span className="text-xs font-semibold text-gray-900 ml-0.5" data-testid="course-rating">{displayTee?.courseRating || '-'}</span>
                  </div>
                  <div className="bg-gray-50 rounded px-2 py-0.5">
                    <span className="text-xs text-gray-500">S:</span>
                    <span className="text-xs font-semibold text-gray-900 ml-0.5" data-testid="course-slope">{displayTee?.slopeRating || '-'}</span>
                  </div>
                  <div className="bg-gray-50 rounded px-2 py-0.5">
                    <span className="text-xs text-gray-500">P:</span>
                    <span className="text-xs font-semibold text-gray-900 ml-0.5" data-testid="course-par">{totalPar}</span>
                  </div>
                </div>
              </div>
              <button
                className="mt-1.5 w-full flex items-center justify-center gap-1 text-xs text-[#064E3B] hover:text-[#065f46] font-medium py-0.5 rounded transition-colors"
                onClick={() => setShowCourseSearch(true)}
                data-testid="button-find-course"
                type="button"
              >
                <Search className="w-3 h-3" />
                Find a course not listed
              </button>
            </div>

            {/* Starting Hole Selector */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
              <div className="flex items-center gap-2">
                <Flag className="w-3 h-3 text-gray-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Start:</span>
                <RadioGroup
                  value={String(startingHole)}
                  onValueChange={(v) => setStartingHole(Number(v) as 1 | 10)}
                  className="flex gap-2 flex-1"
                >
                  <Label
                    htmlFor="start-hole-1"
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1 p-2 rounded-lg border-2 cursor-pointer transition-all',
                      startingHole === 1 ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <RadioGroupItem value="1" id="start-hole-1" className="sr-only" />
                    <span className={cn('font-semibold text-xs', startingHole === 1 ? 'text-blue-600' : 'text-gray-900')}>Hole 1</span>
                  </Label>
                  <Label
                    htmlFor="start-hole-10"
                    className={cn(
                      'flex-1 flex items-center justify-center gap-1 p-2 rounded-lg border-2 cursor-pointer transition-all',
                      startingHole === 10 ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <RadioGroupItem value="10" id="start-hole-10" className="sr-only" />
                    <span className={cn('font-semibold text-xs', startingHole === 10 ? 'text-blue-600' : 'text-gray-900')}>Hole 10</span>
                  </Label>
                </RadioGroup>
                {startingHole === 10 && (
                  <span className="text-xs text-gray-500 flex-shrink-0">(Front=10-18)</span>
                )}
              </div>
            </div>

            {/* Start Fresh button */}
            {(team1Players.some(p => p.name || p.handicap) || team2Players.some(p => p.name || p.handicap)) && (
              <div className="flex justify-start -mb-1">
                <button
                  type="button"
                  onClick={handleStartFresh}
                  className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors py-0.5 px-1"
                  data-testid="button-start-fresh"
                >
                  <RotateCcw className="w-3 h-3" />
                  Clear Players
                </button>
              </div>
            )}

            {/* Recent Players quick-add */}
            {isAuthenticated && recentPlayers.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 px-0.5">Recent Players</p>
                <div
                  className="flex gap-2 overflow-x-auto pb-1"
                  style={{ scrollbarWidth: 'none' }}
                >
                  {recentPlayers.map((p) => {
                    const alreadyAdded = allEnteredNames.includes(p.displayName.trim().toLowerCase());
                    return (
                      <button
                        key={p.personId}
                        type="button"
                        style={{ touchAction: 'manipulation' }}
                        onTouchStart={(e) => {
                          const t = e.touches[0];
                          chipTouchRef.current = { x: t.clientX, y: t.clientY };
                          chipTouchFiredRef.current = false;
                        }}
                        onTouchEnd={(e) => {
                          if (!chipTouchRef.current) return;
                          const t = e.changedTouches[0];
                          const dx = Math.abs(t.clientX - chipTouchRef.current.x);
                          const dy = Math.abs(t.clientY - chipTouchRef.current.y);
                          chipTouchRef.current = null;
                          if (dx > CHIP_SCROLL_THRESHOLD || dy > CHIP_SCROLL_THRESHOLD) return;
                          chipTouchFiredRef.current = true;
                          handleChipTap(p.displayName, p.handicapIndex, alreadyAdded, p.personId);
                        }}
                        onClick={() => {
                          if (chipTouchFiredRef.current) { chipTouchFiredRef.current = false; return; }
                          handleChipTap(p.displayName, p.handicapIndex, alreadyAdded, p.personId);
                        }}
                        className={cn(
                          'flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors',
                          alreadyAdded
                            ? 'border-[#FACC15] bg-yellow-50 text-[#064E3B] font-semibold'
                            : 'border-gray-200 bg-white text-gray-700 active:bg-gray-50'
                        )}
                        data-testid={`chip-recent-${p.personId}`}
                      >
                        {alreadyAdded
                          ? <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                          : null}
                        {p.displayName}
                        {alreadyAdded
                          ? <X className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          : p.handicapIndex != null && (
                              <span className="text-[10px] font-semibold px-1 py-0.5 rounded bg-gray-100 text-gray-500">
                                {p.handicapIndex % 1 === 0 ? p.handicapIndex : p.handicapIndex.toFixed(1)}
                              </span>
                            )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Row 3: Teams */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
              <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
                {matchFormat === '2v2' ? 'Team 1' : 'Player A'}
              </h2>
              <div className="space-y-2">
                {renderPlayerInput('team1', 0, team1Players[0], matchFormat === '2v2' ? 'P1' : '')}
                {matchFormat === '2v2' && renderPlayerInput('team1', 1, team1Players[1], 'P2')}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
              <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
                {matchFormat === '2v2' ? 'Team 2' : 'Player B'}
              </h2>
              <div className="space-y-2">
                {renderPlayerInput('team2', 0, team2Players[0], matchFormat === '2v2' ? 'P1' : '')}
                {matchFormat === '2v2' && renderPlayerInput('team2', 1, team2Players[1], 'P2')}
              </div>
            </div>

            {/* Throw Balls — 2v2, all four names filled */}
            {allFourFilled && (
              <div className="flex flex-col items-center gap-2 -mt-0.5 -mb-0.5">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRandomTeams}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#064E3B] text-[#FACC15] text-xs font-bold hover:bg-[#053f30] active:bg-[#042e22] transition-colors"
                    data-testid="button-throw-balls"
                  >
                    <Shuffle className="w-3.5 h-3.5" />
                    Throw Balls
                  </button>
                  <button
                    type="button"
                    onClick={() => setThrowBallsInfoOpen(true)}
                    className="w-5 h-5 rounded-full bg-[#064E3B] flex items-center justify-center text-[#FACC15] hover:bg-[#053f30] transition-colors flex-shrink-0"
                    data-testid="button-throw-balls-info"
                    aria-label="What is Throw Balls?"
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </div>
                {throwBallsInfoOpen && (
                  <div className="relative bg-[#064E3B] text-white text-xs rounded-xl px-4 py-3 max-w-xs text-center shadow-lg">
                    <button
                      type="button"
                      onClick={() => setThrowBallsInfoOpen(false)}
                      className="absolute top-2 right-2 text-white/60 hover:text-white"
                      aria-label="Close"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <p className="font-semibold mb-1">⛳ Throw Balls</p>
                    <p className="text-white/80 leading-relaxed">A classic golf tradition — each player throws their ball on the first tee. The two closest balls form one team, the two farthest form the other. Tap to randomly assign teams the same way.</p>
                  </div>
                )}
              </div>
            )}

            {/* Row 4: Stakes */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1 flex-shrink-0">
                  <DollarSign className="w-3 h-3" />
                  Stakes
                </span>
                <div className="flex gap-2 flex-1">
                  <div className="flex items-center gap-1 flex-1">
                    <Label htmlFor="stake-front" className="text-xs text-gray-500 flex-shrink-0">F</Label>
                    <div className="relative flex-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                      <Input
                        id="stake-front"
                        type="number"
                        inputMode="numeric"
                        value={stakes.front}
                        onChange={(e) => setStakes({ ...stakes, front: e.target.value })}
                        className="pl-5 text-center h-8 text-sm"
                        data-testid="input-stake-front"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-1">
                    <Label htmlFor="stake-back" className="text-xs text-gray-500 flex-shrink-0">B</Label>
                    <div className="relative flex-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                      <Input
                        id="stake-back"
                        type="number"
                        inputMode="numeric"
                        value={stakes.back}
                        onChange={(e) => setStakes({ ...stakes, back: e.target.value })}
                        className="pl-5 text-center h-8 text-sm"
                        data-testid="input-stake-back"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-1">
                    <Label htmlFor="stake-overall" className="text-xs text-gray-500 flex-shrink-0">O</Label>
                    <div className="relative flex-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                      <Input
                        id="stake-overall"
                        type="number"
                        inputMode="numeric"
                        value={stakes.overall}
                        onChange={(e) => setStakes({ ...stakes, overall: e.target.value })}
                        className="pl-5 text-center h-8 text-sm"
                        data-testid="input-stake-overall"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {isEditMode && onCancelEdit && (
              <Button
                variant="outline"
                onClick={onCancelEdit}
                className="w-full h-10 text-sm font-semibold mb-2"
                data-testid="button-cancel-edit"
              >
                Back to Scorecard
              </Button>
            )}
            {courseRefreshError && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2">
                <p className="text-sm font-semibold text-red-700" data-testid="text-course-refresh-error">{courseRefreshError}</p>
              </div>
            )}
            {validationError && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2">
                <p className="text-sm font-semibold text-red-700" data-testid="text-course-validation-error">{validationError}</p>
              </div>
            )}
            <Button
              onClick={handleStart}
              className="w-full h-12 text-base font-semibold"
              data-testid="button-start-round"
              disabled={(!isEditMode && authLoading) || isRefreshingCourse}
            >
              {isRefreshingCourse ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Confirming course...
                </>
              ) : !isEditMode && authLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                isEditMode ? 'Apply Changes & Resume' : 'Start Round'
              )}
            </Button>
          </div>
          </div>
        </div>
      </div>
      {/* ── Guest warning: round won't be saved ─────────────────────────────── */}
      <Dialog open={showGuestWarning} onOpenChange={setShowGuestWarning}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              Round won't be saved
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>You're not signed in. If you start now, this round will only exist on this device and <strong>will be permanently deleted when you finalize it</strong> — it won't appear in anyone's ledger.</p>
            <p className="text-xs text-gray-500">Sign in first so the round is tracked, and all four players can see results in their history.</p>
          </div>
          <div className="flex flex-col gap-2 pt-1">
            <Button
              className="w-full bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15] font-semibold"
              onClick={() => { setShowGuestWarning(false); window.location.href = '/auth?mode=signin&returnTo=/'; }}
              data-testid="button-guest-warning-signin"
            >
              Sign In to Save Round
            </Button>
            <Button
              variant="outline"
              className="w-full border-red-300 text-red-700 hover:bg-red-50 font-medium text-sm"
              onClick={handlePlayAnyway}
              data-testid="button-guest-warning-play-anyway"
            >
              Play without saving (data will be lost)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showOnDemandInfo} onOpenChange={setShowOnDemandInfo}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">DoublePress! — You're in Control</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>
              If you're losing a bet (Front, Back, or Overall), tap the <strong>Press</strong> button to start a new side bet from that hole forward. You choose the moment — it's fully strategic, i.e. who gets strokes!
            </p>
            <p>
              <strong>Front & Back bets:</strong> Allow press-on-press. If you're losing a press, you can press again, stacking multiple side bets at once.
            </p>
            <p>
              <strong>Overall bet:</strong> Each side gets one Overall press per match, and only when they're losing. Once you use it, it's gone — no press-on-press on Overall.
            </p>
            <p>
              <strong>Catch-up presses at the turn:</strong> If you lost multiple bets on the Front 9, you can fire off multiple Back 9 presses right at Hole 10 to try to get even.
            </p>
            <p>
              <strong>18th Hole Desperation Press:</strong> Before the first player tees off on Hole 18, the losing player or team gets one final opportunity to press up to the total amount they are currently down — including losses from the Front 9, Back 9, Overall, and any active Overall press.
            </p>
            <p>
              Nothing happens automatically — no presses trigger unless someone taps the button.
            </p>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showTwoDownInfo} onOpenChange={setShowTwoDownInfo}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">2 Down Auto — Presses Happen Automatically</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>
              When either side falls two holes behind on a Front or Back bet, a press is created for them without anyone tapping a button.
            </p>
            <p>
              <strong>Front & Back only:</strong> Auto-presses fire on Front 9 and Back 9 bets when a side goes 2 down.
            </p>
            <p>
              <strong>Overall is fixed:</strong> The Overall bet runs all 18 holes with no presses at all — it's a straight match.
            </p>
            <p>
              <strong>No decisions needed:</strong> The system handles it. You just play and score.
            </p>
          </div>
        </DialogContent>
      </Dialog>
      <CourseSearchModal
        open={showCourseSearch}
        onClose={() => {
          setShowCourseSearch(false);
          setCourseSearchQuery('');
        }}
        onImported={(courseId, courseName) => {
          setShowCourseSearch(false);
          setCourseSearchQuery('');
          // The query cache will be invalidated by the modal; once rawCourses
          // refreshes, we need to select the newly imported course.
          // We stash the id so the effect below can pick it up.
          setPendingImportCourseId(courseId);
        }}
        initialQuery={courseSearchQuery}
      />
    </div>
  );
}

export default SetupScreen;
